import React from 'react';
import { Segmented } from '../Segmented';
import { Toggle } from '../../ui/Toggle';
import type { Persona, Tone } from '../../../types/settings';
const PREVIEW: Record<Tone, {
  kort: string;
  lang: string;
}> = {
  warm: {
    kort: 'Goedemorgen. Om half tien je eerste afspraak, en het blijft droog.',
    lang: 'Goedemorgen! Je eerste afspraak is om half tien. Het blijft vandaag droog, dus fietsen kan prima. Zal ik je om negen uur herinneren?'
  },
  neutraal: {
    kort: 'Eerste afspraak 09:30. Droog vandaag.',
    lang: 'Je eerste afspraak is om 09:30. Vandaag blijft het droog. Wil je een herinnering om 09:00?'
  },
  zakelijk: {
    kort: 'Afspraak 09:30. Geen neerslag.',
    lang: 'Agenda: eerste afspraak 09:30. Weer: geen neerslag verwacht. Herinnering instellen voor 09:00?'
  }
};
interface PersonaPanelProps {
  persona: Persona;
  onChange: (p: Persona) => void;
}
export function PersonaPanel({
  persona,
  onChange
}: PersonaPanelProps) {
  const set = <K extends keyof Persona,>(k: K, v: Persona[K]) => onChange({
    ...persona,
    [k]: v
  });
  let preview = persona.verbosity > 0.5 ? PREVIEW[persona.tone].lang : PREVIEW[persona.tone].kort;
  if (persona.address === 'u') preview = preview.replace(/\bJe\b/g, 'Uw').replace(/\bje\b/g, 'u');
  if (persona.humor && persona.tone !== 'zakelijk') preview += ' Paraplu mag thuisblijven.';
  return <div className="grid gap-8 rounded-3xl border border-white/[0.07] bg-night-900 p-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:p-8" data-id="element-118" data-mp="3upzk3ezphje2">
      <dl className="space-y-5 text-[14px]" data-id="element-119" data-mp="7jp0zdh07byj">
        <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-120" data-mp="7guihrhjw17r">
          <dt className="text-ink-soft" data-id="element-121" data-mp="m6ib84bo1rjv">Toon</dt>
          <dd data-id="element-122" data-mp="h8qkhdmrcbh9">
            <Segmented label="Toon" value={persona.tone} onChange={v => set('tone', v)} options={[{
            value: 'warm',
            label: 'Warm'
          }, {
            value: 'neutraal',
            label: 'Neutraal'
          }, {
            value: 'zakelijk',
            label: 'Zakelijk'
          }]} data-id="element-123" data-mp="h8707q9mxb02" />
          </dd>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-124" data-mp="1397bn3z6djhn">
          <dt className="text-ink-soft" data-id="element-125" data-mp="13739aqgxcsjh">Aanspreekvorm</dt>
          <dd data-id="element-126" data-mp="1879249yxkl35">
            <Segmented label="Aanspreekvorm" value={persona.address} onChange={v => set('address', v)} options={[{
            value: 'je',
            label: 'Je'
          }, {
            value: 'u',
            label: 'U'
          }]} data-id="element-127" data-mp="186pkyvh3u0q6" />
          </dd>
        </div>
        <div data-id="element-128" data-mp="1m1rz25w3uwe2">
          <dt className="flex items-center justify-between text-ink-soft" data-id="element-129" data-mp="1m1i4e5048121">
            <label htmlFor="verbosity" data-id="element-130" data-mp="1qzjn2otmpycz">Lengte van antwoorden</label>
            <span className="text-[13px] text-ink-muted" data-id="element-131" data-mp="1vwtol2uam0h6">{persona.verbosity > 0.5 ? 'Uitgebreid' : 'Kort'}</span>
          </dt>
          <dd className="mt-2" data-id="element-132" data-mp="3srzivlsx68oy">
            <input id="verbosity" type="range" min={0} max={1} step={0.05} value={persona.verbosity} onChange={e => set('verbosity', Number(e.target.value))} className="w-full accent-[rgb(var(--accent))]" data-id="element-133" data-mp="1ju3iu758e2i" />
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3" data-id="element-134" data-mp="262bh254fsc4t">
          <dt className="text-ink-soft" data-id="element-135" data-mp="213i1861og91h">Lichte humor</dt>
          <dd data-id="element-136" data-mp="210vto6esdtj9">
            <Toggle label="Humor" checked={persona.humor} onChange={v => set('humor', v)} hideLabelOnMobile data-id="element-137" data-mp="2xspmdcydwg4v" />
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3" data-id="element-138" data-mp="2tsp8btf766yc">
          <dt className="text-ink-soft" data-id="element-139" data-mp="2os1aoowgqrv5">Begroet je ’s ochtends uit zichzelf</dt>
          <dd data-id="element-140" data-mp="2jt6qpjmrex9g">
            <Toggle label="Begroeten" checked={persona.proactiveGreeting} onChange={v => set('proactiveGreeting', v)} hideLabelOnMobile data-id="element-141" data-mp="2jtgfj3w2iydk" />
          </dd>
        </div>
      </dl>

      <figure className="flex flex-col justify-center border-t border-white/[0.06] pt-6 lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0" data-id="element-142" data-mp="1darr82dc3ztu">
        <figcaption className="text-[13px] text-ink-muted" data-id="element-143" data-mp="2wah93x29jypx">Zo klinkt Anna nu</figcaption>
        <blockquote className="mt-3 font-display text-[24px] font-light leading-snug text-ink" data-id="element-144" data-mp="311zagm8781bf">“{preview}”</blockquote>
        <p className="mt-4 text-[13px] text-ink-muted" data-id="element-145" data-mp="2nb3xoyukx02k">Anna blijft altijd eerlijk over wat ze wel en niet mag, ongeacht de toon.</p>
      </figure>
    </div>;
}